import { useState, useEffect, useCallback } from 'react';
import { navigationConfig } from '@/config/navigation';

const SESSION_KEY = 'eb-nav-history';
const MAX_HISTORY = 4;

export interface HistoryEntry {
  path: string;
  label: string;
}

/**
 * Resolves a route path to a human-readable page label.
 * Only resolves actual pages (items with a `path`).
 * Parent group items (items with only `children`, no `path`) are excluded.
 * Handles /projects/:id and sub-routes.
 */
function resolveLabel(path: string): string | null {
  // Dashboard / Homepage
  if (path === '/dashboard' || path === '/') return 'Homepage';

  // Flatten all nav items that have a `path` (real pages only)
  const flatPages: { path: string; label: string }[] = [];

  const flatten = (items: typeof navigationConfig.sections[0]['items']) => {
    for (const item of items) {
      if (item.path) {
        flatPages.push({ path: item.path, label: item.label });
      }
      if (item.children) {
        flatten(item.children);
      }
    }
  };

  for (const section of navigationConfig.sections) {
    flatten(section.items);
  }

  // Exact match first
  const exact = flatPages.find((p) => p.path === path);
  if (exact) return exact.label;

  // Project detail pages: /projects/:id or /projects/:id/anything
  if (path.startsWith('/projects/')) return 'Project Details';

  // Settings hub
  if (path === '/settings') return 'Settings';

  return null;
}

function readHistory(): HistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as HistoryEntry[];
  } catch {
    return [];
  }
}

function writeHistory(entries: HistoryEntry[]) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(entries));
  } catch {
    // sessionStorage unavailable — silently ignore
  }
}

/**
 * Tracks the last MAX_HISTORY pages visited, including Homepage.
 * Homepage is treated exactly like any other page.
 * Returns the history array and a navigateTo function.
 *
 * - Consecutive duplicate visits are collapsed.
 * - Parent group items (Resources, Reports, Settings group) are never recorded.
 * - Clicking a breadcrumb that already exists in the trail truncates all entries
 *   after it rather than appending a new entry at the end.
 */
export function useNavigationHistory(currentPath: string) {
  const [history, setHistory] = useState<HistoryEntry[]>(readHistory);

  const recordVisit = useCallback((path: string) => {
    const label = resolveLabel(path);

    // Skip any path we can't resolve to a real page label
    if (!label) return;

    setHistory((prev) => {
      // Don't push if the resolved label is the same as the current last entry
      // (handles / → /dashboard redirect both resolving to 'Homepage')
      if (prev.length > 0 && prev[prev.length - 1].label === label) return prev;

      // If the path already exists earlier in the trail, truncate at that point
      // (handles back-navigation via breadcrumb click — removes entries after it)
      const existingIndex = prev.findIndex((e) => e.path === path);
      if (existingIndex !== -1) {
        const truncated = prev.slice(0, existingIndex + 1);
        writeHistory(truncated);
        return truncated;
      }

      const entry: HistoryEntry = { path, label };
      const next = [...prev, entry];

      // Keep only the last MAX_HISTORY entries
      const trimmed = next.slice(-MAX_HISTORY);
      writeHistory(trimmed);
      return trimmed;
    });
  }, []);

  // Record visit whenever currentPath changes
  useEffect(() => {
    recordVisit(currentPath);
  }, [currentPath, recordVisit]);

  return history;
}
